// ⬇️ Existing imports
import { useCallback, useEffect, useState } from 'react';
import {
  useAdvanceBoolean,
  useBoolean,
  useWealthlaneCustomerApi,
  useWealthlaneIdentityApi,
} from './hooks';
import { useDispatch, useSelector } from 'react-redux';
import Navbar from './components/navbar';
import Spinners from './components/spinner';
import { selectAuthRoutesList, setRouteList } from './pages/configuration/routeSlice';
import { InnerContainer } from './constants/style/common.styled';
import RoutesContainer from './routes/RoutesContainer.js';
import AuthService, { syncSessionFromLocalStorage } from './services/authService';
import {
  sessionChannel,
  shouldBroadcastNewSession,
  broadcastNewSession,
  handleSessionMessage,
  type SessionEventType,
} from './services/sessionCoordinator';
import { AbpApplicationConfigurationService } from './services/wealthlane-identity-services';
import { getFilteredPermissionData } from './utils';
import pingBg from '../src/assets/images/pink_background.png';
import { ParticipantLoginLogService } from './services/wealthlane-customer-services';
import { isEmpty } from 'lodash';
import { useLocation, useNavigate } from 'react-router-dom';
import { selectToken, setCredentials } from './auth/authSlice';
import { selectNotify } from './auth/notify';
import AlertPopup from './components/alert-popup';
import {
  REDIRECT_PERMISSION_MESSAGE,
  RELOAD_PERMISSION_MESSAGE,
  LOGOUT_NOTIFICATION_MESSAGE,
  NEW_SESSION_NOTIFICATION_MESSAGE,
  PPTVIEW_SESSION_ENDED_MESSAGE,
} from './constants/common';
import { axiosInstance, killSession } from './services/apiInterceptor';
import { showErrorModal, subscribeRetry } from './components/error-modal/errorModalEmitter';
import useForceInit from './hooks/custom/useForceInit';
import {
  selectTenantId,
  setParticipantCredentials,
} from './pages/configuration/participantSignupSlice';
import { BrandingProvider } from './context/brandingContext';
import DisplaySettingsGate from './context/displaySettingsGate';
import { prefetchDisplaySettings } from './hooks/get/useGetDisplaySettings';
import { queryClient } from './queryClient';

function App(): JSX.Element {
  const location = useLocation();
  const PUBLIC_ROUTES = [
    '/sign-up',
    '/sign-up-confirm',
    '/email-confirm',
    '/login',
    '/callback',
    '/redirect',
  ];
  const isPublicRoute = PUBLIC_ROUTES.some((route) => location.pathname.startsWith(route));
  const [isNotify, setNotify] = useState(false);
  const authService = new AuthService();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  useForceInit();

  const token = useSelector(selectToken);
  const id = useSelector(selectTenantId);
  const showNotify = useSelector(selectNotify);
  const userAuthList = useSelector(selectAuthRoutesList);
  const { isLoading, handleRequest } = useWealthlaneIdentityApi();
  const [permissionStatus, setPermissionStatus] = useState<string>('');
  const { handleRequest: CustomerHandleRequest } = useWealthlaneCustomerApi();
  const { value: isModalOpen, setFalse: closeModal, setTrue: openModal } = useBoolean(false);
  const {
    value: isLogoutModalOpen,
    setFalse: closeLogoutModal,
    setTrue: openLogoutModal,
  } = useBoolean(false);

  const {
    value: showAlert,
    setFalse: closeAlert,
    setTrue: openAlert,
    customValue: alertMessage,
    setCustomValue: setAlertMessage,
  } = useAdvanceBoolean(false);

  const {
    value: isConfirmOpen,
    setFalse: closeConfirmModal,
    setTrue: openConfirmModal,
    customValue,
    setCustomValue,
  } = useAdvanceBoolean<string>(false);

  const [myCompanyResponse, setMyCompanyResponse] = useState<any>();
  const [selectedTenant, setSelectedTenant] = useState<any>();
  const [selectedParticipantId, setSelectedParticipantId] = useState<any>();
  const [selectedEsppParticipantId, setSelectedEsppParticipantId] = useState<any>();

  const [isTenantSet, setIsTenant] = useState<boolean>(Boolean(localStorage.getItem('tenantId')));
  const isPptView = userAuthList?.extraProperties?.[`ImpersonationInfo:ParticipantId`];
  const tenantId = useSelector(selectTenantId);

  const signInApi = (): void => {
    if (id != 'undefined' && id != null) {
      CustomerHandleRequest(
        ParticipantLoginLogService.postApiCustomerApiAppParticipantLoginLogLog()
      )
        .then((data: any) => {
          window.sessionStorage.setItem('uniqueLogin', data);
        })
        .catch((error: any) => {
          console.log('error', error);
        });
    }
  };

  const fetchMyCompanies = useCallback(() => {
    if (token != null && !isPublicRoute) {
      const tenantHeader =
        sessionStorage.getItem('tenantToken') || localStorage.getItem('tenantToken') || '';

      axiosInstance
        .get(
          `${process.env.REACT_APP_WEALTHLANE_API_ENDPOINT}/api/customer/api/app/participant-company-appservice/my-companies`,
          {
            headers: {
              'X-Tenant-Id': tenantHeader,
            },
          }
        )
        .then((response) => {
          setMyCompanyResponse(response.data);

          response?.data.length > 1
            ? openConfirmModal()
            : dispatch(
                setParticipantCredentials({
                  tenantId: response.data?.[0]?.tenantId,
                  participantId: response.data?.[0]?.participantId,
                  esppParticipantId: response.data?.[0]?.esppParticipantId,
                })
              );

          setIsTenant(true);
        })
        .catch((error) => {
          const validationMsg = error?.response?.data?.error?.validationErrors?.[0]?.message;
          if (validationMsg) {
            setAlertMessage(validationMsg);
            openAlert();
          } else {
            showErrorModal({ status: error?.response?.status });
          }
        });
    }
  }, [token]);

  useEffect(() => {
    fetchMyCompanies();
  }, [fetchMyCompanies]);

  useEffect(() => {
    return subscribeRetry(fetchMyCompanies);
  }, [fetchMyCompanies]);

  useEffect((): void => {
    syncSessionFromLocalStorage();
    authService
      .getUser()
      .then((user) => {
        if (user != null) {
          syncSessionFromLocalStorage();
          dispatch(
            setCredentials({
              isAuthenticated: authService.isAuthenticated(),
              token: authService.getToken(),
              user,
            })
          );
          if (shouldBroadcastNewSession()) {
            broadcastNewSession();
          }
        }
      })
      .catch((err) => {
        if (err == null) void authService.signOutRedirectCallback();
      });
    setNotify(true);
  }, []);

  useEffect(() => {
    if (token != null && !isPublicRoute) {
      handleRequest(
        AbpApplicationConfigurationService.getApiIdentityApiAbpApplicationConfiguration()
      )
        .then((response) => {
          const { nonFieldLevelPermissionsGroups } = getFilteredPermissionData(
            response?.auth?.grantedPolicies
          );

          const RouteList = Object.keys(nonFieldLevelPermissionsGroups).map((item) => item);
          dispatch(
            setRouteList({
              currentUserAuthList: response ?? {},
              currentUserRoutesList: RouteList ?? [],
            })
          );
        })
        .catch((err) => {
          console.log('err', err);
        });
    }
  }, [token]);

  useEffect(() => {
    token &&
      tenantId &&
      !isPublicRoute &&
      isEmpty(sessionStorage.getItem('uniqueLogin')) &&
      signInApi();
  }, [token, tenantId]);

  // HTF-1482: start loading the display precision together with branding (same condition as
  // BrandingProvider) so DisplaySettingsGate does not wait for branding first.
  useEffect(() => {
    if (token && tenantId) void prefetchDisplaySettings(queryClient, tenantId);
  }, [token, tenantId]);

  useEffect(() => {
    if (token != null && !isPublicRoute && userAuthList.currentUser?.isAuthenticated === false) {
      void authService.logout();
    }
  }, [userAuthList, token]);

  useEffect(() => {
    document.getElementsByTagName('html')[0].style.overflow = 'unset';
  }, [location]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  const [crossTabEvent, setCrossTabEvent] = useState<SessionEventType | null>(null);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const eventType = handleSessionMessage(event);
      if (eventType != null) {
        killSession();
        setCrossTabEvent(eventType);
        openLogoutModal();
      }
    };

    sessionChannel.addEventListener('message', handleMessage);
    return () => {
      sessionChannel.removeEventListener('message', handleMessage);
    };
  }, []);

  useEffect(() => {
    const handler = (evt: any) => {
      setPermissionStatus(evt.detail);
      openModal();
    };
    window.addEventListener('PERMISSION_CHANGED', handler);
    return () => window.removeEventListener('PERMISSION_CHANGED', handler);
  }, []);

  const handleCloseModal = () => {
    closeModal();
    if (permissionStatus === 'SIGN_IN') {
      authService.signInSilent();
    } else if (permissionStatus === 'REFRESH') {
      navigate('/');
      navigate(0);
    } else {
      navigate(0);
    }
  };

  const onCrossTabDismiss = () => {
    closeLogoutModal();

    if (isPptView) {
      sessionStorage.clear();
      window.location.replace(
        `${process.env.REACT_APP_AUTH_URL}/Account/LoggedOut`
      );
      return;
    }

    if (crossTabEvent === 'new_session_event') {
      syncSessionFromLocalStorage();
      window.location.replace('/overview');
    } else {
      sessionStorage.clear();
      window.location.replace('/');
    }
  };

  const onPrimaryClick = (values: Record<string, any>): void => {
    dispatch(
      setParticipantCredentials({
        tenantId: selectedTenant,
        participantId: selectedParticipantId,
        esppParticipantId: selectedEsppParticipantId,
      })
    );
    closeConfirmModal();
  };

  useEffect(() => {
    if (isPptView) {
      const historyLoggedInTime = localStorage.getItem('historyLoggedInTime');
      if (historyLoggedInTime) {
        localStorage.setItem('loggedIn Time', historyLoggedInTime);
      }
    }
  }, [isPptView, userAuthList]);

  return (
    <div>
      {isConfirmOpen && (
        <AlertPopup
          isShow={isConfirmOpen}
          closeModal={closeConfirmModal}
          customTitle={'Choose your tenant'}
          handleSubmit={onPrimaryClick}
          chooseTenant={true}
          optionData={myCompanyResponse}
          setSelectedTenant={setSelectedTenant}
          selectedTenant={selectedTenant}
          setSelectedParticipantId={setSelectedParticipantId}
          setSelectedEsppParticipantId={setSelectedEsppParticipantId}
          isPptView={isPptView}
        />
      )}

      {!isPublicRoute && (isLoading || (token && !isTenantSet)) ? (
        <Spinners containerClassName='h-screen' />
      ) : (
        <BrandingProvider enabled={Boolean(token) && Boolean(tenantId)}>
          <DisplaySettingsGate enabled={Boolean(token) && Boolean(tenantId)} tenantId={tenantId}>
            <Navbar
              isAuth={authService.isAuthenticated()}
              showNotify={showNotify}
              isNotify={isNotify}
            />
            <div id='main-content'>
              <InnerContainer img={pingBg} hasNotify={showNotify}>
                <RoutesContainer />
              </InnerContainer>

              {isModalOpen && !isPptView && (
                <AlertPopup
                  isShow={isModalOpen}
                  ConfirmTitle='Ok'
                  customTitle={
                    permissionStatus === 'RELOAD'
                      ? RELOAD_PERMISSION_MESSAGE
                      : REDIRECT_PERMISSION_MESSAGE
                  }
                  handleSubmit={() => handleCloseModal()}
                />
              )}
            </div>
          </DisplaySettingsGate>
        </BrandingProvider>
      )}
      {showAlert && (
        <AlertPopup
          isShow={showAlert}
          centerHeading
          ConfirmTitle='Logout'
          customTitle={alertMessage}
          handleSubmit={() => authService.logout({ force: true })}
        />
      )}
      {isLogoutModalOpen && (
        <AlertPopup
          isShow={isLogoutModalOpen}
          ConfirmTitle={'Ok'}
          customTitle={
            crossTabEvent === 'new_session_event'
              ? NEW_SESSION_NOTIFICATION_MESSAGE
              : isPptView
                ? PPTVIEW_SESSION_ENDED_MESSAGE
                : LOGOUT_NOTIFICATION_MESSAGE
          }
          handleSubmit={onCrossTabDismiss}
          isPptView={isPptView}
        />
      )}
    </div>
  );
}

export default App;
